import { IconButton, Stack, Tooltip } from "@mui/material";
import { RxOpenInNewWindow } from "react-icons/rx";
import { SiNaver } from "react-icons/si";
import { TbMapSearch } from "react-icons/tb";
import { paths } from "src/paths";
import { FavoriteAptStarButton } from "src/components/favorite-apt-star-button";
import { buildAptMapSearchUrl } from "src/components/apt-map-search-link";

/** 표시할 액션 수에 맞는 CustomTable 정보 컬럼 권장 폭을 반환한다. */
export const getAptActionColumnWidth = ({
  showAptWindow = true,
  showNaver = true,
  showMapSearch = true,
  showStar = true,
} = {}) => {
  const count = [showAptWindow, showNaver, showMapSearch, showStar]
    .filter(Boolean).length;
  return ({ 0: 0, 1: 32, 2: 48, 3: 62, 4: 76 })[count];
};

/** 새창·네이버·지도검색·별표 네 액션을 표시하는 테이블 컬럼 권장 폭이다. */
export const APT_LINK_COLUMN_WIDTH = getAptActionColumnWidth();

/**
 * 아파트 새창 + 네이버 링크 + 지도검색 + 관심아파트 별표 아이콘 버튼
 * @param {string} aptId - 아파트 ID (필수)
 * @param {string} pyeongId - 평형 ID (선택, 새창 URL에 포함)
 * @param {string} aptName - 아파트명 (별표 다이얼로그용)
 * @param {number} supplyPyeong - 평형 (별표 다이얼로그용)
 * @param {number} iconSize - 아이콘 크기 (기본 12)
 * @param {boolean} showAptWindow - 아파트 새창 버튼 표시 여부 (기본 true)
 * @param {boolean} showNaver - 네이버 부동산 버튼 표시 여부 (기본 true)
 * @param {boolean} showMapSearch - 지도검색 버튼 표시 여부 (기본 true)
 * @param {boolean} showStar - 별표 표시 여부 (기본 false)
 * @param {function} onOpen - 새창 열기 함수 (기본 window.open). router.newWindow 등을 전달 가능.
 */
export const AptLinkButtons = ({
  aptId,
  pyeongId,
  aptName,
  supplyPyeong,
  iconSize = 12,
  showAptWindow = true,
  showNaver = true,
  showMapSearch = true,
  showStar = false,
  onOpen,
  sx,
}) => {
  if (!aptId) return null;

  const openAptWindow = (url) => {
    if (onOpen) {
      onOpen(url, 880, window.innerHeight);
    } else {
      window.open(url, "_blank", `width=880,height=${window.innerHeight}`);
    }
  };

  const openNaverWindow = (url) => {
    if (onOpen) {
      onOpen(url, 1000);
    } else {
      window.open(url, "_blank", "width=1000");
    }
  };

  /** 지도검색 화면을 메뉴 없는 넓은 새창으로 연다. */
  const openMapSearchWindow = (url) => {
    if (onOpen) {
      onOpen(url, 1200, 800);
    } else {
      window.open(url, "_blank", "width=1200,height=800");
    }
  };

  const newWindowUrl = pyeongId
    ? `${paths.dashboard.apts.newwindow}?id=${aptId}&pyeongId=${pyeongId}&menu=false`
    : `${paths.dashboard.apts.newwindow}?id=${aptId}&menu=false`;

  const naverUrl = `${paths.naver}${aptId}`;
  const mapSearchUrl = buildAptMapSearchUrl(aptId, pyeongId);

  return (
    <Stack
      direction="row"
      spacing={0.3}
      sx={sx}
    >
      {showAptWindow && (
        <Tooltip title="아파트 새창">
          <IconButton
            size="small"
            sx={{ p: 0.2 }}
            onClick={(e) => {
              e.stopPropagation();
              openAptWindow(newWindowUrl);
            }}
          >
            <RxOpenInNewWindow size={iconSize} />
          </IconButton>
        </Tooltip>
      )}
      {showNaver && (
        <Tooltip title="네이버 부동산">
          <IconButton
            size="small"
            sx={{ p: 0.2 }}
            onClick={(e) => {
              e.stopPropagation();
              openNaverWindow(naverUrl);
            }}
          >
            <SiNaver size={iconSize - 2} />
          </IconButton>
        </Tooltip>
      )}
      {showMapSearch && (
        <Tooltip title="지도검색">
          <IconButton
            size="small"
            sx={{ p: 0.2 }}
            onClick={(e) => {
              e.stopPropagation();
              openMapSearchWindow(mapSearchUrl);
            }}
          >
            <TbMapSearch size={iconSize + 1} />
          </IconButton>
        </Tooltip>
      )}
      {showStar && (
        <FavoriteAptStarButton
          aptId={aptId}
          pyeongId={pyeongId}
          aptName={aptName}
          supplyPyeong={supplyPyeong}
          iconSize={iconSize}
        />
      )}
    </Stack>
  );
};
